import axios from 'axios';

/**
 * Unified Governance API Client
 * 
 * Single axios instance for all /api/ai/* endpoints used by governance pages:
 * - Dashboard, Organizations, Tenants, Agents, Budgets, Confidence, Audit
 * 
 * Features:
 * - Auto-injects Authorization header from SUPERADMIN token (separate from CRM)
 * - Handles 401 with auto-logout
 * - Consistent error handling
 * 
 * ISOLATION: Uses 'superadmin_token' NOT 'access_token'
 * 
 * PHASE 2 ADDITIONS:
 * - Budget editing endpoints
 * - Confidence threshold tuning
 * - Agent resume controls
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Create the governance API client
const governanceApi = axios.create({
  baseURL: `${API_BASE}/api/ai`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - inject SUPERADMIN auth token (NOT CRM token)
governanceApi.interceptors.request.use(
  (config) => {
    // CRITICAL: Use superadmin_token, NOT access_token
    const token = localStorage.getItem('superadmin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 auto-logout (redirect to superadmin login)
governanceApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.log('[GovernanceAPI] Auth failed - redirecting to SuperAdmin login');
      localStorage.removeItem('superadmin_token');
      localStorage.removeItem('superadmin_user');
      window.location.href = '/superadmin/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// BILLING RECONCILIATION API (governor-only)
// Separate client because these live under /api/tenant/billing (NOT /api/ai),
// but still authenticate with the SAME governance-scoped superadmin token.
// ============================================
const billingGovApi = axios.create({
  baseURL: `${API_BASE}/api/tenant/billing`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});
billingGovApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('superadmin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}, (error) => Promise.reject(error));

// ============================================
// GOVERNANCE API METHODS
// ============================================

export const governanceAPI = {
  // ----- ORPHAN USERS (phantom-tenant early warning) -----
  getOrphanUsers: () => governanceApi.get('/ops/sprint1/orphan-users'),

  // ----- BILLING DRIFT (reconciliation) -----
  getBillingDriftReport: () => billingGovApi.get('/reconciliation/report'),
  getBillingAudit: () => governanceApi.get('/ops/billing-audit'),
  enforceTenantReconciliation: (tenantId) =>
    billingGovApi.post(`/reconciliation/enforce?tenant_id=${encodeURIComponent(tenantId)}`),
  voidPhantomInvoices: (tenantId, dryRun = true) =>
    billingGovApi.post(`/reconciliation/void-phantom-invoices?tenant_id=${encodeURIComponent(tenantId)}&dry_run=${dryRun}`),
  backfillRenewals: (tenantId) =>
    billingGovApi.post(`/reconciliation/backfill-renewals${tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ''}`),

  // ----- CREDIT & REFUND REPAIR (superadmin, audited) -----
  getCreditStatus: (tenantId) =>
    governanceApi.get(`/ops/billing/credit-status/${encodeURIComponent(tenantId)}`),
  adjustCredit: (tenantId, payload) =>
    governanceApi.post(`/ops/billing/credit-adjust/${encodeURIComponent(tenantId)}`, payload),
  reconcileRefunds: (tenantId, dryRun = true) =>
    governanceApi.post(`/ops/billing/refund-reconcile/${encodeURIComponent(tenantId)}?dry_run=${dryRun}`),

  // ----- USAGE AUDIT (universal-key / LLM credit) -----
  getUniversalCreditAudit: (date, thresholdUsd) => {
    const p = new URLSearchParams();
    if (date) p.set('date', date);
    if (thresholdUsd != null && thresholdUsd !== '') p.set('threshold_usd', thresholdUsd);
    const qs = p.toString();
    return governanceApi.get(`/universal-credit-audit${qs ? `?${qs}` : ''}`);
  },
  getUniversalCreditTrend: (days = 30, top = 5) =>
    governanceApi.get(`/universal-credit-trend?days=${days}&top=${top}`),
  getLLMSpendAlertConfig: () => governanceApi.get('/llm-spend-alert-config'),
  setLLMSpendAlertConfig: (thresholdUsd, enabled) =>
    governanceApi.put('/llm-spend-alert-config', { threshold_usd: thresholdUsd, enabled }),
  getLLMSpendAlerts: (limit = 20) => governanceApi.get(`/llm-spend-alerts?limit=${limit}`),
  runLLMSpendAlertCheck: (date) =>
    governanceApi.post(`/llm-spend-alerts/run${date ? `?date=${date}` : ''}`),

  // ----- DASHBOARD -----
  getKillSwitches: () => governanceApi.get('/kill-switches'),

  // ----- ECONOMICS (Phase 2: Unit Economics + Capability Inspector) -----
  getEconomicsOverview: (days = 30) => governanceApi.get(`/ops/economics/overview?days=${days}`),
  getEconomicsTenants: (days = 30) => governanceApi.get(`/ops/economics/tenants?days=${days}`),
  getEconomicsTenantDetail: (tenantId, days = 30) => governanceApi.get(`/ops/economics/tenant/${tenantId}?days=${days}`),
  getToolMargins: (days = 30) => governanceApi.get(`/ops/economics/tool-margins?days=${days}`),
  getCapability: (tenantId) => governanceApi.get(`/ops/economics/capability/${tenantId}`),
  getBudgetAlertsSummary: () => governanceApi.get('/budget-alerts/summary'),
  getSalesMetrics: (days = 30) => governanceApi.get(`/metrics/sales?days=${days}`),
  getSupportMetrics: (days = 30) => governanceApi.get(`/metrics/support?days=${days}`),
  
  // Enhanced Dashboard Metrics (NEW)
  getDashboardMetrics: (timeRange = '7d', startDate = null, endDate = null) => {
    let url = `/dashboard/metrics?time_range=${timeRange}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    return governanceApi.get(url);
  },
  getQuickStats: () => governanceApi.get('/dashboard/quick-stats'),

  // ----- ORGANIZATIONS -----
  getOrganizations: () => governanceApi.get('/orgs'),
  getOrganizationsEnhanced: () => governanceApi.get('/orgs/enhanced'),
  getOrganization: (orgId) => governanceApi.get(`/orgs/${orgId}`),
  getOrganizationDetail: (orgId) => governanceApi.get(`/orgs/${orgId}/detail`),
  createOrganization: (data) => governanceApi.post('/orgs', data),
  updateOrganization: (orgId, data) => governanceApi.patch(`/orgs/${orgId}`, data),
  getOrgPolicies: (orgId) => governanceApi.get(`/orgs/${orgId}/policies`),
  getOrgAudit: (orgId, limit = 50) => governanceApi.get(`/orgs/${orgId}/audit?limit=${limit}`),
  executeOrgAction: (orgId, data) => governanceApi.post(`/orgs/${orgId}/actions`, data),

  // ----- TENANTS -----
  getTenants: (orgId = null) => {
    const params = orgId ? `?org_id=${orgId}` : '';
    return governanceApi.get(`/tenants${params}`);
  },
  getTenantsEnhanced: (opts = {}) => {
    const { orgId = null, q = '', plan = 'all', billing = 'all', demo = 'all', hideTest = false, skip = 0, limit = 50 } = opts;
    const p = new URLSearchParams();
    if (orgId) p.set('org_id', orgId);
    if (q) p.set('q', q);
    if (plan && plan !== 'all') p.set('plan', plan);
    if (billing && billing !== 'all') p.set('billing', billing);
    if (demo && demo !== 'all') p.set('demo', demo);
    if (hideTest) p.set('hide_test', 'true');
    p.set('skip', String(skip));
    p.set('limit', String(limit));
    return governanceApi.get(`/tenants/enhanced?${p.toString()}`);
  },
  getTenantDetail: (tenantId) => governanceApi.get(`/tenants/${tenantId}/detail`),
  getTenantBillingHistory: (tenantId) => governanceApi.get(`/tenants/${tenantId}/billing-history`),
  getTenantsByOrg: (orgId) => governanceApi.get(`/tenants?org_id=${orgId}`),
  createTenant: (orgId, data) => governanceApi.post(`/orgs/${orgId}/tenants`, data),

  // ----- USERS -----
  getUsers: (tenantId = null) => {
    const params = tenantId ? `?tenant_id=${tenantId}` : '';
    return governanceApi.get(`/users${params}`);
  },
  getUserDetail: (userId) => governanceApi.get(`/users/${userId}/detail`),

  // ----- AGENTS (Phase 2: Resume Controls) -----
  getAutoPausedAgents: () => governanceApi.get('/confidence/auto-paused'),
  resumeAgent: (agentName, data) => 
    governanceApi.post(`/confidence/${agentName}/resume`, data),
  getAgentConfidence: (agentName) => governanceApi.get(`/confidence/${agentName}`),
  getAgentEvents: (agentName, limit = 50) => governanceApi.get(`/agent-events/${agentName}?limit=${limit}`),

  // ----- BUDGETS (Phase 2: Budget Editing) -----
  getBudgetAlerts: () => governanceApi.get('/budget-alerts'),
  acknowledgeBudgetAlert: (alertId) => governanceApi.post(`/budget-alerts/${alertId}/acknowledge`),
  bulkAcknowledgeAlerts: (alertIds) => governanceApi.post('/budget-alerts/bulk-acknowledge', alertIds),
  getBudgetState: (tenantId = null) => {
    return tenantId 
      ? governanceApi.get(`/budget-state/${tenantId}`)
      : governanceApi.get('/budget-state');
  },
  getTenantBudget: (tenantId) => governanceApi.get(`/budget-state/${tenantId}`),
  
  // Enhanced Budget Dashboard
  getEnhancedBudgetDashboard: (timeRange = '30d', severity = null, page = 1, pageSize = 20) => {
    let url = `/budget-dashboard/enhanced?time_range=${timeRange}&page=${page}&page_size=${pageSize}`;
    if (severity && severity !== 'all') {
      url += `&severity=${severity}`;
    }
    return governanceApi.get(url);
  },
  
  // Phase 2: Budget editing
  updateTenantBudget: (tenantId, data) => governanceApi.put(`/budget/${tenantId}`, data),
  resetTenantBudget: (tenantId) => governanceApi.post(`/budget/${tenantId}/reset`),

  // ----- CONFIDENCE (Phase 2: Threshold Tuning) -----
  getConfidenceStats: () => governanceApi.get('/confidence/auto-paused'),
  getConfidenceLockConfig: () => governanceApi.get('/confidence/lock-config'),
  getConfidenceDashboardPolicy: () => governanceApi.get('/confidence/dashboard-policy'),
  
  // Enhanced Confidence Dashboard
  getEnhancedConfidenceDashboard: (timeRange = '30d') => 
    governanceApi.get(`/confidence/dashboard/enhanced?time_range=${timeRange}`),
  
  // Phase 2: Threshold editing
  setAgentConfidenceThreshold: (agentName, data) => 
    governanceApi.post(`/confidence/${agentName}/threshold`, data),
  setGlobalConfidenceThreshold: (data) => 
    governanceApi.post('/confidence/global/threshold', data),

  // ----- AUDIT -----
  getAuditLogs: () => governanceApi.get('/audit'),
  getSuperAdminAudit: () => governanceApi.get('/superadmin/audit'),
  
  // Unified Audit API with pagination, filtering, and analytics
  getUnifiedAuditLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    if (params.action) queryParams.append('action', params.action);
    if (params.scope) queryParams.append('scope', params.scope);
    if (params.result) queryParams.append('result', params.result);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.tenant_id) queryParams.append('tenant_id', params.tenant_id);
    if (params.org_id) queryParams.append('org_id', params.org_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.search) queryParams.append('search', params.search);
    return governanceApi.get(`/audit/unified?${queryParams.toString()}`);
  },
  
  // Export audit logs as CSV or JSON
  exportAuditLogs: async (format = 'csv', filters = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('format', format);
    if (filters.action) queryParams.append('action', filters.action);
    if (filters.scope) queryParams.append('scope', filters.scope);
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    
    const response = await governanceApi.get(`/audit/export?${queryParams.toString()}`, {
      responseType: 'blob'
    });
    return response;
  },

  // ----- SUPERADMIN ACTIONS -----
  executeSuperAdminAction: (data) => governanceApi.post('/superadmin', data),
  
  // ----- COST USAGE -----
  getCostUsage: () => governanceApi.get('/cost-usage'),
  getTenantCostUsage: (tenantId) => governanceApi.get(`/cost-usage/${tenantId}`),
  getCostTransactions: (tenantId = null, limit = 100) => {
    const params = tenantId ? `?tenant_id=${tenantId}&limit=${limit}` : `?limit=${limit}`;
    return governanceApi.get(`/cost-transactions${params}`);
  },

  // ----- AUTONOMY & MARGINS (Credit-Spine flip instrument) -----
  getCreditSpineMargins: (days = 30, tenantId = null) => {
    const params = tenantId ? `?days=${days}&tenant_id=${tenantId}` : `?days=${days}`;
    return governanceApi.get(`/ops/economics/credit-spine-margins${params}`);
  },
  getOrchestratorDecisions: (days = 7, limit = 100, tenantId = null) => {
    const tp = tenantId ? `&tenant_id=${tenantId}` : '';
    return governanceApi.get(`/ops/economics/orchestrator-decisions?days=${days}&limit=${limit}${tp}`);
  },

  // ----- INVESTOR VIEW -----
  getInvestorSummary: () => governanceApi.get('/demo/investor/summary'),
  getInvestorShowcase: () => governanceApi.get('/demo/investor/demo-showcase'),
  getEnhancedInvestorDashboard: (timeRange = '30d') => 
    governanceApi.get(`/demo/investor/dashboard/enhanced?time_range=${timeRange}`),
  exportInvestorReport: async (format = 'csv') => {
    const response = await governanceApi.get(`/demo/investor/export?format=${format}`, {
      responseType: 'blob'
    });
    return response;
  },

  // ----- SCHEDULED ACTIONS -----
  getScheduledActions: () => governanceApi.get('/scheduled-actions'),
  triggerScheduledAction: (actionName) => governanceApi.post(`/scheduled-actions/${actionName}/trigger`),
  getScheduledActionLog: (limit = 50) => governanceApi.get(`/scheduled-actions/log?limit=${limit}`),

  // ----- AI APPROVALS -----
  getPendingApprovals: (limit = 50) => governanceApi.get(`/approvals/pending?limit=${limit}`),
  getMyApprovalRequests: (limit = 50) => governanceApi.get(`/approvals/my-requests?limit=${limit}`),
  approveRequest: (approvalId, data) => governanceApi.post(`/approvals/${approvalId}/approve`, data),
  rejectRequest: (approvalId, data) => governanceApi.post(`/approvals/${approvalId}/reject`, data),
  getApprovalSettings: () => governanceApi.get('/approvals/settings'),
  updateApprovalSettings: (data) => governanceApi.put('/approvals/settings', data),
  getPendingApprovalCount: () => governanceApi.get('/approvals/pending/count'),

  // ----- GOVERNOR OPS (Phase 7) -----
  // Third-party vendor health ping (Apollo, Claude, Stripe, SendGrid, Twilio, ElevenLabs)
  getVendorHealth: (force = false) => governanceApi.get(`/ops/vendor-health${force ? '?force=true' : ''}`),
  // Onboarding + brain_jobs stuck > N minutes
  getStuckJobs: (minutes = 5) => governanceApi.get(`/ops/stuck-jobs?minutes=${minutes}`),
  getPastDueAlerts: () => governanceApi.get('/ops/past-due-alerts'),
  resolvePastDueAlert: (alertId) => governanceApi.post(`/ops/past-due-alerts/${alertId}/resolve`),
  getBillingIntegrity: () => governanceApi.get('/ops/billing-integrity'),
  rollbackBillingIntegrity: (tenantId) =>
    governanceApi.post(`/ops/billing-integrity/${encodeURIComponent(tenantId)}/rollback`),
  relinkBillingIntegrity: (tenantId) =>
    governanceApi.post(`/ops/billing-integrity/${encodeURIComponent(tenantId)}/relink`),
  resolveBillingIntegrity: (tenantId) =>
    governanceApi.post(`/ops/billing-integrity/${encodeURIComponent(tenantId)}/resolve`),
  getBillingTimeline: (tenantId) =>
    governanceApi.get(`/ops/billing-timeline/${encodeURIComponent(tenantId)}`),
  fixBillingTimeline: (tenantId) =>
    governanceApi.post(`/ops/billing-timeline/${encodeURIComponent(tenantId)}/fix`),
  reconcileInvoices: (tenantId, dryRun = true, voidPhantoms = false) =>
    governanceApi.post(`/ops/billing/reconcile-invoices/${encodeURIComponent(tenantId)}?dry_run=${dryRun}&void_phantoms=${voidPhantoms}`),
  getBillingIntegrityCount: () => governanceApi.get('/ops/billing-integrity/count'),
  lookupTenants: (q) => governanceApi.get(`/ops/tenant-lookup?q=${encodeURIComponent(q)}`),
  getBillingOverride: (tenantId) =>
    governanceApi.get(`/ops/billing-override/${encodeURIComponent(tenantId)}`),
  applyBillingOverride: (tenantId, payload) =>
    governanceApi.post(`/ops/billing-override/${encodeURIComponent(tenantId)}`, payload),
  getBrainReliability: () => governanceApi.get('/ops/brain-reliability'),
  // Re-enqueue onboarding for a tenant
  retryTenantOnboarding: (tenantId, reason) =>
    governanceApi.post(`/ops/tenants/${tenantId}/retry-onboarding`, { reason }),
  // Grant USD credits to a tenant
  grantTenantCredits: (tenantId, amount_usd, reason) =>
    governanceApi.post(`/ops/tenants/${tenantId}/grant-credits`, { amount_usd, reason }),
  // Issue a short-lived CRM token to log in as the tenant admin
  impersonateTenant: (tenantId, reason, duration_minutes = 15) =>
    governanceApi.post(`/ops/tenants/${tenantId}/impersonate`, { reason, duration_minutes }),

  // ----- APOLLO SPEND TRACKING (Phase 2 — Iter 55) -----
  // Aggregated Apollo API spend over the last N days (governor only).
  // Returns: { window_days, summary[], top_tenants[], total_calls, total_provider_cost_usd, total_tenant_charged_usd }
  getApolloSpend: (days = 7) => governanceApi.get(`/ops/apollo-spend?days=${days}`),

  // ----- ITER 62.4 SPRINT 1 — Founder / Team / Investor visibility -----
  // A9 — Search any user / tenant / org / lead across the platform
  crossTenantSearch: (q, limit = 20) =>
    governanceApi.get(`/ops/sprint1/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  // B6 — Cohort retention heatmap (investor-grade)
  getCohortRetention: (monthsBack = 6) =>
    governanceApi.get(`/ops/sprint1/investor/cohort-retention?months_back=${monthsBack}`),
  // D1+D2 — Unified integration hub (Apollo / Stripe / SendGrid / Twilio / ElevenLabs / Razorpay + extension heartbeats)
  getIntegrationHub: () => governanceApi.get('/ops/sprint1/integration-hub'),
  // A5 — Onboarding funnel drop-off
  getOnboardingFunnel: (days = 30) =>
    governanceApi.get(`/ops/sprint1/onboarding-funnel?days=${days}`),
  // E3 — Top errors aggregator
  getTopErrors: (hours = 24, topN = 10) =>
    governanceApi.get(`/ops/sprint1/observability/top-errors?hours=${hours}&top_n=${topN}`),
  // E5 — Rolling uptime SLA
  getUptimeSla: (days = 30) =>
    governanceApi.get(`/ops/sprint1/observability/uptime?days=${days}`),

  // ----- HELP INSIGHTS (docs usage: most-opened guides + 👍/👎 + comments) -----
  getHelpInsights: () => governanceApi.get('/ops/help-insights'),

  // ----- DOCS SCREENSHOTS (one-click refresh of Help Center product shots) -----
  refreshHelpScreenshots: () => governanceApi.post('/ops/help-screenshots/refresh'),
  getHelpScreenshotStatus: () => governanceApi.get('/ops/help-screenshots/status'),

  // ----- VOICELINK — adopt an existing DID for a tenant (cross-tenant, governor) -----
  // Uses raw axios (NOT the governanceApi instance) so a validation 403 does NOT
  // trip the instance's auto-logout interceptor. Hits the CRM-area path directly
  // with the governance token (backend accepts it via get_governance_user).
  adoptVoicelinkForTenant: (payload) => {
    const token = localStorage.getItem('superadmin_token');
    return axios.post(`${API_BASE}/api/voice/voicelink/adopt-for-tenant`, payload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
  },

  // ----- VOICELINK — Force-restore a number that's already owned+routed on VoiceLink -----
  // Brings the tenant LIVE without the purchase/map steps (no DID id needed). Governor-scoped.
  forceRestoreVoicelinkForTenant: (payload) => {
    const token = localStorage.getItem('superadmin_token');
    return axios.post(`${API_BASE}/api/voice/voicelink/force-restore`, payload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
  },

  // ----- VOICELINK — Reconnect existing client (governor one-click trunk+routing) -----
  // Raw axios + superadmin token (these live under /api/voice/voicelink, not /api/ai).
  listVoicelinkClients: () => {
    const token = localStorage.getItem('superadmin_token');
    return axios.get(`${API_BASE}/api/voice/voicelink/reseller-clients`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  getVoicelinkClientInventory: (clientId) => {
    const token = localStorage.getItem('superadmin_token');
    return axios.get(`${API_BASE}/api/voice/voicelink/client-inventory?client_id=${encodeURIComponent(clientId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  reconnectVoicelinkClient: (payload) => {
    const token = localStorage.getItem('superadmin_token');
    return axios.post(`${API_BASE}/api/voice/voicelink/reconnect-client`, payload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
  },
};

export default governanceApi;
// Rebuild trigger: 1770632883
